// 월 이용료 화면 — 달마다 고정된 청구서 목록을 보여 주고, 카드(토스 결제창) 또는 계좌이체(세금계산서) 요청으로 받는다
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getCurrentOrgId, orgQuery } from "@/lib/org";
import { getDocs, where } from "firebase/firestore";
import { downloadXlsx } from "@/lib/exportXlsx";
import { fmtDateTime } from "@/lib/format";
import { plainError } from "@/lib/plainError";
import {
  confirmBillingPayment,
  getBillingInvoice,
  koDueDate,
  openPaymentWindow,
  reportBillingDispute,
  requestTransferPayment,
  type BillingResult,
  type InvoiceView,
} from "@/lib/billing";

const won = (n: number) => `${n.toLocaleString()}원`;
const CONFIRMING_CALL_MS = 30 * 60 * 1000; // '확인 중'이 이보다 길면 사람이 볼 차례 — 전화 안내
const errMsg = (e: unknown) => plainError(e);

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<BillingResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err" | "info"; text: string } | null>(null);
  const confirming = useRef(false);

  // ym을 주면 그달 청구서를 서버가 정리(확인 중이면 토스에 조회)한 뒤 돌려준다. 없으면 기본(지난달).
  const load = useCallback(async (ym?: string) => {
    try {
      setData(await getBillingInvoice(ym));
    } catch (e) {
      setMsg({ kind: "err", text: `청구서를 불러오지 못했습니다 — ${errMsg(e)}` });
    }
  }, []);

  // 토스 결제창에서 돌아온 경우: 성공이면 서버 승인, 실패면 사유 표시. 그다음 주소창을 비운다.
  useEffect(() => {
    const result = params.get("result");
    if (!result) {
      load();
      return;
    }
    if (result === "fail") {
      setMsg({ kind: "err", text: `결제가 취소되었거나 실패했습니다 — ${params.get("message") || "사유 없음"}. 돈은 나가지 않았습니다.` });
      setParams({}, { replace: true });
      load();
      return;
    }
    const paymentKey = params.get("paymentKey");
    const orderId = params.get("orderId");
    const amount = Number(params.get("amount"));
    if (confirming.current) return;
    if (!paymentKey || !orderId || !amount) {
      setMsg({ kind: "err", text: "결제 결과 정보가 빠져 있습니다. 결제는 진행되지 않았습니다." });
      setParams({}, { replace: true });
      load();
      return;
    }
    confirming.current = true;
    setViewYm(orderId.split("_")[1] || "");
    setMsg({ kind: "info", text: "결제를 확인하는 중입니다. 창을 닫지 마세요…" });
    confirmBillingPayment({ paymentKey, orderId, amount })
      .then((r) => {
        if (r.ok) setMsg({ kind: "ok", text: "결제가 완료되었습니다. 감사합니다." });
        else if (r.pending) setMsg({ kind: "info", text: "결제 확인이 끝나면 결제 완료로 바뀝니다. 두 번 결제하지 마세요." });
      })
      .catch((e) => setMsg({ kind: "err", text: errMsg(e) }))
      .finally(() => {
        setParams({}, { replace: true });
        load(orderId.split("_")[1]);
      });
  }, [params, setParams, load]);

  const [viewYm, setViewYm] = useState<string>("");

  // 청구 근거 명단 — 그달 배송 기록에서 학생을 한 번씩만 뽑는다(청구서의 studentCount와 같은 기준, 예행연습 03-7)
  const [basis, setBasis] = useState<{ ym: string; rows: { 학교: string; 이름: string; 첫배송: string; 배송횟수: number }[] } | null>(null);
  const loadBasis = async (ym: string) => {
    if (basis?.ym === ym) { setBasis(null); return; }
    const snap = await getDocs(orgQuery("deliveries", where("yearMonth", "==", ym)));
    const byStudent = new Map<string, { 학교: string; 이름: string; 첫배송: number; 배송횟수: number }>();
    snap.forEach((d) => {
      const x = d.data() as { studentId?: string; school?: string; studentName?: string; deliveredAt?: { toMillis?: () => number } };
      if (!x.studentId) return;
      const at = x.deliveredAt?.toMillis?.() ?? 0;
      const cur = byStudent.get(x.studentId);
      if (cur) { cur.배송횟수++; if (at && at < cur.첫배송) cur.첫배송 = at; }
      else byStudent.set(x.studentId, { 학교: x.school || "", 이름: x.studentName || "", 첫배송: at, 배송횟수: 1 });
    });
    const rows = [...byStudent.values()]
      .sort((a, b) => a.학교.localeCompare(b.학교) || a.이름.localeCompare(b.이름))
      .map((r) => ({ 학교: r.학교, 이름: r.이름, 첫배송: r.첫배송 ? fmtDateTime(new Date(r.첫배송)) : "", 배송횟수: r.배송횟수 }));
    setBasis({ ym, rows });
  };

  const pay = async (ym: string) => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      // 누르는 순간 그달 청구서를 다시 받아 주문번호를 확인한다(금액은 한 번 만든 뒤 바뀌지 않는다)
      const fresh = await getBillingInvoice(ym);
      setData(fresh);
      if (!fresh.invoice || fresh.invoice.status !== "ready") return;
      await openPaymentWindow(fresh.invoice, getCurrentOrgId(), (m) => setMsg({ kind: "err", text: m }));
    } catch (e) {
      setMsg({ kind: "err", text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const transfer = async (ym: string, cancel = false) => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await requestTransferPayment(ym, cancel);
      if (cancel) {
        setMsg(
          r.notified
            ? { kind: "ok", text: "계좌이체 요청을 취소하고 담당자에게 알렸습니다. 카드로 결제하실 수 있습니다." }
            : { kind: "err", text: "계좌이체 요청을 취소했지만 담당자에게 알림이 가지 않았습니다. 카드로 결제하실 수 있고, 010-2608-5099로 취소했다고 알려 주세요." },
        );
        await load();
        return;
      }
      setMsg(
        r.notified
          ? { kind: "ok", text: "계좌이체 요청을 받았습니다. 입금 계좌를 문자로 안내해 드리고, 입금이 확인되면 세금계산서를 보내 드립니다." }
          : { kind: "err", text: "요청은 저장했지만 담당자에게 알림이 가지 않았습니다. 번거로우시겠지만 010-2608-5099로 전화 주세요." },
      );
      await load();
    } catch (e) {
      setMsg({ kind: "err", text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  // 「금액이 이상해요」 — 사유를 적어 보내면 기록되고 콕소프트에 문자가 간다(대표 결정 Q16)
  const [disputeFor, setDisputeFor] = useState("");
  const [disputeText, setDisputeText] = useState("");
  const sendDispute = async (ym: string) => {
    if (busy) return;
    if (disputeText.trim().length < 2) { setMsg({ kind: "err", text: "어디가 이상한지 짧게라도 적어 주세요." }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const r = await reportBillingDispute(ym, disputeText.trim());
      setMsg(
        r.notified
          ? { kind: "ok", text: r.already ? "이 달 금액 문의는 이미 받아 확인 중입니다. 결과는 이 화면과 전화로 알려 드립니다." : "금액 문의를 받았습니다. 콕소프트가 확인해 결과를 이 화면과 전화로 알려 드립니다." }
          : { kind: "err", text: "문의는 저장했지만 담당자에게 알림이 가지 않았습니다. 번거로우시겠지만 010-2608-5099로 전화 주세요." },
      );
      setDisputeFor("");
      setDisputeText("");
      await load(ym);
    } catch (e) {
      setMsg({ kind: "err", text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const history = data?.history || [];
  const inv: InvoiceView | null | undefined = history.find((h) => h.yearMonth === viewYm) || data?.invoice;
  const statusText = (i: InvoiceView) =>
    i.status === "paid" ? "결제 완료" : i.status === "free" ? "첫 달 무료" : i.status === "confirming" ? "확인 중"
      : i.status === "none" ? "청구 없음" : i.transferRequested ? "계좌이체 요청됨" : "결제 전";
  const usageText = (i: InvoiceView) => {
    const u = i.usage || {};
    const parts = [u.ata ? `카카오 알림톡 ${u.ata}건` : "", u.sms ? `짧은 문자 ${u.sms}건` : "", u.lms ? `긴 문자 ${u.lms}건` : ""].filter(Boolean);
    return parts.length ? parts.join(" · ") : "없음";
  };

  return (
    <div className="max-w-xl space-y-4">
      <h1 className="text-lg font-extrabold">💳 이용료</h1>

      {msg && (
        <div className={"card p-4 text-sm " + (msg.kind === "ok" ? "text-emerald-700" : msg.kind === "err" ? "text-red-600" : "text-slate-600")} role="status">
          {msg.text}
        </div>
      )}

      {!data && !msg && <div className="card p-5 text-sm text-slate-400">불러오는 중…</div>}

      {data?.exempt && <div className="card p-5 text-sm">이 업체는 이용료를 받지 않는 업체입니다.</div>}

      {inv && (
        <div className="card p-5 space-y-3 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="font-semibold text-base">{inv.yearMonth} 이용료</span>
            <span className={inv.status === "paid" || inv.status === "free" ? "text-emerald-700 font-bold" : "text-slate-500"}>{statusText(inv)}</span>
          </div>
          <table className="w-full">
            <tbody>
              <tr><td className="py-1 text-slate-500">배송한 학생 {inv.studentCount.toLocaleString()}명 × {won(inv.unitPrice)}</td><td className="py-1 text-right">{won(inv.studentSupply ?? inv.supplyAmount)}</td></tr>
              {inv.noticePrices && (
                <tr><td className="py-1 text-slate-500">보호자 알림 {usageText(inv)}</td><td className="py-1 text-right">{won(inv.noticeSupply ?? 0)}</td></tr>
              )}
              <tr><td className="py-1 text-slate-500">공급가</td><td className="py-1 text-right">{won(inv.supplyAmount)}</td></tr>
              <tr><td className="py-1 text-slate-500">부가세(10%)</td><td className="py-1 text-right">{won(inv.vat)}</td></tr>
              <tr className="border-t"><td className="pt-2 font-bold">결제할 금액</td><td className="pt-2 text-right text-lg font-extrabold">{won(inv.amount)}</td></tr>
            </tbody>
          </table>
          {inv.studentCount > 0 && (
            <div className="text-sm">
              <button className="underline text-milk-700 min-h-[36px]" onClick={() => loadBasis(inv.yearMonth)}>
                {basis?.ym === inv.yearMonth ? "명단 접기" : `청구 근거 — 배송한 학생 ${inv.studentCount.toLocaleString()}명 명단 보기`}
              </button>
              {basis?.ym === inv.yearMonth && (
                <div className="mt-2 rounded-xl bg-slate-50 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-slate-600">그달 배송 기록에 한 번이라도 있는 학생입니다({basis.rows.length}명).</span>
                    <button className="btn-secondary text-xs" onClick={() => downloadXlsx(`이용료근거_${inv.yearMonth}_${basis.rows.length}명.xlsx`, `${inv.yearMonth} 근거`, basis.rows)}>엑셀로 받기</button>
                  </div>
                  <ul className="max-h-48 overflow-auto divide-y text-xs">
                    {basis.rows.map((r, i) => (
                      <li key={i} className="py-1 flex justify-between gap-2"><span>{r.학교} {r.이름}</span><span className="text-slate-500">{r.첫배송}{r.배송횟수 > 1 ? ` · ${r.배송횟수}회` : ""}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {inv.status === "free" && <p className="text-emerald-700">첫 달은 이용료와 알림 요금을 받지 않습니다. 다음 달부터 청구됩니다.</p>}
          {inv.noticePrices
            ? <p className="text-slate-500">알림 요금은 문자 1건 {won(inv.noticePrices.sms)} · 카카오 알림톡 1건 {won(inv.noticePrices.ata)}(부가세 별도)입니다.</p>
            : <p className="text-slate-500">그달 보호자 알림: {usageText(inv)} <span className="text-slate-400">(이 달 청구서에는 알림 요금이 없습니다)</span></p>}

          {inv.status === "ready" && (
            <p className="rounded-xl bg-slate-50 p-3">
              {inv.dueDate && <><b>결제 기한 {koDueDate(inv.dueDate)}</b>(청구일부터 14일). </>}
              자동으로 빠져나가지 않습니다 — 매달 이 화면에서 직접 결제하거나 계좌이체를 요청해 주세요.
              {inv.dueDate && " 기한 3일 전과 당일에 사장님 휴대폰으로 다시 알려 드립니다."}
            </p>
          )}
          {inv.status === "ready" && !inv.transferRequested && (
            <div className="grid gap-2">
              <button className="btn-primary w-full" onClick={() => pay(inv.yearMonth)} disabled={busy}>
                {busy ? "처리 중…" : `카드로 ${won(inv.amount)} 결제하기`}
              </button>
              <button className="btn-secondary w-full" onClick={() => transfer(inv.yearMonth)} disabled={busy}>
                계좌이체로 내기(세금계산서)
              </button>
            </div>
          )}
          {inv.status === "ready" && inv.transferRequested && (
            <div className="rounded-xl bg-milk-50 p-3 space-y-2">
              <p>계좌이체 요청을 받았습니다. 입금 계좌를 문자로 안내해 드리고, 입금이 확인되면 이 화면이 「결제 완료」로 바뀌고 세금계산서를 보내 드립니다.</p>
              {inv.transferLocked ? (
                // 계좌 안내가 나간 뒤에는 앱에서 취소하지 않는다 — 입금한 뒤 취소하고 카드로 또 내는 이중 결제를 막는다(대표 결정 Q7)
                <p className="text-slate-600">
                  이미 계좌 안내가 나갔습니다(또는 곧 문자로 갑니다). 카드 결제는 막혀 있습니다(이중 결제 방지). 취소나 카드로 바꾸기는{" "}
                  <a className="underline font-semibold" href="tel:01026085099">010-2608-5099</a>로 전화해 요청해 주세요. 이미 입금하셨으면 그대로 두시면 됩니다.
                </p>
              ) : (
                <>
                  <p className="text-slate-500">카드 결제는 막혀 있습니다(이중 결제 방지). 카드로 내시려면 요청을 취소하세요.</p>
                  <button className="btn-secondary w-full" onClick={() => transfer(inv.yearMonth, true)} disabled={busy}>계좌이체 요청 취소</button>
                </>
              )}
            </div>
          )}
          {inv.status === "confirming" && (
            <div className="rounded-xl bg-milk-50 p-3 space-y-2">
              {inv.confirmingAt && Date.now() - inv.confirmingAt > CONFIRMING_CALL_MS ? (
                <p>결제 확인이 30분 넘게 끝나지 않았습니다. 두 번 결제하지 마시고 <a className="underline font-semibold" href="tel:01026085099">010-2608-5099</a>로 전화 주세요. 결제된 돈이 있으면 그대로 반영하고, 없으면 다시 결제할 수 있게 풀어 드립니다.</p>
              ) : (
                <p className="text-slate-600">결제 결과를 확인하고 있습니다. 두 번 결제하지 마세요. 5분쯤 뒤 「다시 확인」을 누르면 결과가 정리됩니다.</p>
              )}
              <button className="btn-secondary w-full" onClick={() => load(inv.yearMonth)} disabled={busy}>다시 확인</button>
            </div>
          )}
          {inv.status === "paid" && inv.method === "계좌이체" && (
            <p className="text-emerald-700">계좌이체로 받았습니다. 세금계산서는 환경 설정에 적으신 이메일로 보내 드립니다.</p>
          )}
          {inv.status === "paid" && inv.method !== "계좌이체" && inv.receiptUrl && (
            <a className="text-emerald-700 underline" href={inv.receiptUrl} target="_blank" rel="noreferrer">
              {inv.approvedAt ? `${new Date(inv.approvedAt).toLocaleDateString("ko-KR")} · ` : ""}{inv.method || "카드"} 결제 — 영수증 보기
            </a>
          )}
          {(data?.disputes || []).filter((d) => d.yearMonth === inv.yearMonth).map((d, i) => (
            <div key={i} className={"rounded-xl p-3 space-y-1 " + (d.status === "open" ? "bg-amber-50" : "bg-slate-50")}>
              <div className="font-semibold">
                금액 문의 {d.status === "open" ? "— 콕소프트가 확인 중" : "— 처리됨"}
                {d.createdAt ? <span className="font-normal text-slate-500"> · {fmtDateTime(new Date(d.createdAt))}</span> : null}
              </div>
              <div className="text-slate-600 break-keep">“{d.reason}”</div>
              {d.resolution && <div className="text-slate-800 break-keep">처리 결과: {d.resolution}</div>}
            </div>
          ))}
          {inv.status !== "none" && !(data?.disputes || []).some((d) => d.yearMonth === inv.yearMonth && d.status === "open") && (
            disputeFor === inv.yearMonth ? (
              <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                <label className="block">
                  <span className="font-semibold">어디가 이상한가요?</span>
                  <textarea
                    className="input mt-1 w-full"
                    rows={3}
                    maxLength={500}
                    placeholder="예: 전출한 학생 3명이 들어가 있어요 / 두 번 결제됐어요"
                    value={disputeText}
                    onChange={(e) => setDisputeText(e.target.value)}
                  />
                </label>
                <p className="text-slate-500">보내면 콕소프트가 확인합니다. 그동안 이 청구서 금액은 그대로 보입니다.</p>
                <div className="flex gap-2">
                  <button className="btn-primary flex-1" onClick={() => sendDispute(inv.yearMonth)} disabled={busy}>보내기</button>
                  <button className="btn-secondary flex-1" onClick={() => { setDisputeFor(""); setDisputeText(""); }} disabled={busy}>닫기</button>
                </div>
              </div>
            ) : (
              <button className="underline text-slate-600 min-h-[44px]" onClick={() => setDisputeFor(inv.yearMonth)}>금액이 이상해요</button>
            )
          )}
          <p className="text-sm text-slate-500">
            달이 끝나면 다음 달 1일에 청구서가 만들어집니다. 청구서 금액은 자동으로 바뀌지 않습니다. 잘못 청구됐거나 두 번 결제된 것이 확인되면
            이용약관 제8조에 따라 돌려드리거나 깎아 드립니다 — 「금액이 이상해요」로 알려 주세요.{" "}
            <a className="underline" href="tel:01026085099">문의 010-2608-5099 (평일 9시~18시)</a>
          </p>
        </div>
      )}

      {history.length > 1 && (
        <div className="card p-4 text-sm">
          <div className="font-semibold mb-2">지난 청구서</div>
          <ul className="divide-y">
            {history.map((h) => (
              <li key={h.yearMonth}>
                <button className="w-full flex justify-between py-3 min-h-[44px] text-left" onClick={() => setViewYm(h.yearMonth)}>
                  <span>{h.yearMonth} · {won(h.amount)}</span>
                  <span className={h.status === "ready" ? "text-rose-600 font-semibold" : "text-slate-500"}>{statusText(h)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && (
        <div className="card p-4 text-sm text-slate-500">
          이번 달({data.current.yearMonth}) 지금까지 배송한 학생 {data.current.studentCount.toLocaleString()}명 — 달이 끝나면 청구됩니다.
        </div>
      )}
    </div>
  );
}
